import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, LogOut, Shield, Key, User, AlertTriangle, RefreshCw, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ProSetupModal from "@/components/ProSetupModal";
import RegenCodesModal from "@/components/RegenCodesModal";
import Pro2FASetupModal from "@/components/Pro2FASetupModal";
import Pro2FADisableModal from "@/components/Pro2FADisableModal";
import ReferralWidget from "@/components/ReferralWidget";

export default function ProDashboardPage() {
  const navigate = useNavigate();
  const [gateState, setGateState] = useState(null);
  const [account, setAccount] = useState(null);
  const [recoveryStatus, setRecoveryStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FADisable, setShow2FADisable] = useState(false);

  useEffect(() => {
    const checkGate = async () => {
      try {
        const { data } = await api.get("/pro/gate-status");
        setGateState(data);
        if (!data.authenticated) {
          navigate("/pro/login", { replace: true });
          return;
        }
        if (!data.initialized) {
          setShowSetup(true);
          setLoading(false);
          return;
        }
        await loadDashboard();
      } catch {
        navigate("/pro/login", { replace: true });
      }
    };
    checkGate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDashboard = useCallback(async () => {
    try {
      const [meRes, recRes] = await Promise.all([
        api.get("/pro/me"),
        api.get("/pro/recovery-status").catch(() => null),
      ]);
      setAccount(meRes.data);
      if (recRes) setRecoveryStatus(recRes.data);
    } catch {
      navigate("/pro/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleSetupComplete = async () => {
    setShowSetup(false);
    // Post-payment flow terminates here: jump to the ProReadyScreen which
    // auto-forwards to /pro/inbox. Dashboard is no longer a dead-end.
    navigate("/pro/ready", { replace: true });
  };

  const handleRegenComplete = async () => {
    setShowRegen(false);
    try {
      const { data } = await api.get("/pro/recovery-status");
      setRecoveryStatus(data);
    } catch { /* ignore */ }
    toast.success("Recovery codes regenerated");
  };

  const handle2FASetupComplete = async () => {
    setShow2FASetup(false);
    setLoading(true);
    await loadDashboard();
  };

  const handle2FADisableComplete = async () => {
    setShow2FADisable(false);
    setLoading(true);
    await loadDashboard();
  };

  const handleLogout = async () => {
    try {
      await api.post("/pro/logout");
      toast.success("Logged out");
    } catch { /* ignore */ }
    navigate("/pro/login", { replace: true });
  };

  if (showSetup) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]" data-testid="pro-dashboard-setup">
        <ProSetupModal open={true} onComplete={handleSetupComplete} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center" data-testid="pro-dashboard-loading">
        <Loader2 size={24} className="animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!account) return null;

  const remaining = recoveryStatus?.remaining_codes ?? 10;
  const isCritical = remaining <= 2;
  const isLow = remaining <= 3 && remaining > 2;

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="pro-dashboard">
      {/* Header */}
      <header className="border-b border-[#E5E5E5] bg-white">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-zinc-900 flex items-center justify-center">
              <Shield size={16} className="text-white" />
            </div>
            <span className="font-semibold text-[#111111] text-sm">QuickInbox PRO</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-zinc-500 hover:text-[#555555] text-xs gap-1.5"
            data-testid="pro-dashboard-logout"
          >
            <LogOut size={14} /> Log out
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <ReferralWidget />
        {/* Critical warning: <= 2 codes */}
        {isCritical && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3" data-testid="recovery-banner-critical">
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
              <AlertTriangle size={16} className="text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-red-900">
                {remaining === 0 ? "No recovery codes remaining" : `Only ${remaining} recovery ${remaining === 1 ? "code" : "codes"} remaining`}
              </p>
              <p className="text-xs text-red-600 mt-0.5">
                {remaining === 0
                  ? "You cannot recover your account if you lose your password. Regenerate codes now."
                  : "If you use your remaining codes, you won't be able to recover your account. Regenerate now while you're logged in."}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setShowRegen(true)}
              className="bg-red-600 hover:bg-red-700 text-white text-xs shrink-0 gap-1.5"
              data-testid="recovery-banner-regen-btn"
            >
              <RefreshCw size={13} /> Regenerate
            </Button>
          </div>
        )}

        {/* Soft notice: 3 codes */}
        {isLow && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="recovery-banner-low">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
              <Key size={16} className="text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-900">Recovery codes running low</p>
              <p className="text-xs text-amber-600 mt-0.5">
                You have {remaining} recovery codes left. Consider regenerating while you're logged in.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowRegen(true)}
              className="border-amber-300 text-amber-800 hover:bg-amber-100 text-xs shrink-0 gap-1.5"
              data-testid="recovery-banner-regen-btn"
            >
              <RefreshCw size={13} /> Regenerate
            </Button>
          </div>
        )}

        {/* Welcome */}
        <div>
          <h1 className="text-xl font-semibold text-[#111111]" data-testid="pro-dashboard-welcome">
            Welcome, {account.username}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Your PRO account is active and secured.</p>
        </div>

        {/* Account Card */}
        <div className="bg-white rounded-xl border border-[#E5E5E5] divide-y divide-[#E5E5E5] shadow-sm">
          <div className="px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center">
              <User size={16} className="text-[#888888]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#111111]">{account.username}</p>
              <p className="text-xs text-zinc-500">
                Member since {account.created_at ? new Date(account.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—"}
              </p>
            </div>
            <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-0.5" data-testid="pro-dashboard-status">
              Active
            </span>
          </div>

          {/* 2FA Status */}
          <div className="px-5 py-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${account.tfa_enabled ? "bg-emerald-50" : "bg-[#F5F5F5]"}`}>
              {account.tfa_enabled ? <ShieldCheck size={16} className="text-emerald-600" /> : <Shield size={16} className="text-[#888888]" />}
            </div>
            <div className="flex-1">
              <p className="text-sm text-[#555555]">Two-factor authentication</p>
              <p className="text-xs text-zinc-500">
                {account.tfa_enabled ? "Protected with authenticator app" : "Not configured yet"}
              </p>
            </div>
            {account.tfa_enabled ? (
              <button
                onClick={() => setShow2FADisable(true)}
                className="text-[10px] font-medium text-red-500 hover:text-red-600 flex items-center gap-1"
                data-testid="2fa-disable-link"
              >
                <ShieldOff size={11} /> Disable
              </button>
            ) : (
              <Button
                size="sm"
                onClick={() => setShow2FASetup(true)}
                className="bg-zinc-900 hover:bg-zinc-800 text-white text-[10px] h-7 px-3"
                data-testid="2fa-enable-btn"
              >
                <Shield size={11} className="mr-1" /> Enable 2FA
              </Button>
            )}
          </div>

          {/* Recovery Codes */}
          {recoveryStatus && (
            <div className="px-5 py-4 flex items-center gap-3" data-testid="pro-dashboard-recovery">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isCritical ? "bg-red-50" : isLow ? "bg-amber-50" : "bg-[#F5F5F5]"}`}>
                <Key size={16} className={isCritical ? "text-red-600" : isLow ? "text-amber-600" : "text-[#888888]"} />
              </div>
              <div className="flex-1">
                <p className="text-sm text-[#555555]">Recovery codes</p>
                <p className="text-xs text-zinc-500">
                  {remaining} of {recoveryStatus.total_codes} remaining
                </p>
              </div>
              {(isCritical || isLow) ? (
                <button
                  onClick={() => setShowRegen(true)}
                  className={`text-[10px] font-medium flex items-center gap-1 ${isCritical ? "text-red-600" : "text-amber-600"}`}
                  data-testid="recovery-row-regen-link"
                >
                  <RefreshCw size={11} /> Regenerate
                </button>
              ) : (
                <button
                  onClick={() => setShowRegen(true)}
                  className="text-[10px] font-medium text-zinc-500 hover:text-[#888888] flex items-center gap-1"
                  data-testid="recovery-row-regen-link"
                >
                  <RefreshCw size={11} /> Regenerate
                </button>
              )}
            </div>
          )}
        </div>

        {/* Quick action: Open inbox */}
        <div className="bg-white rounded-xl border border-[#E5E5E5] shadow-sm p-5 flex items-center justify-between gap-4" data-testid="pro-dashboard-open-inbox">
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#111111]">Your inbox</p>
            <p className="text-xs text-zinc-500 mt-0.5">Read messages sent to your PRO alias.</p>
          </div>
          <Button
            onClick={() => navigate("/pro/inbox")}
            className="bg-zinc-900 hover:bg-zinc-800 text-white h-9 text-xs shrink-0"
            data-testid="pro-dashboard-inbox-btn"
          >
            Open inbox
          </Button>
        </div>
      </main>

      {/* Regenerate Codes Modal */}
      <RegenCodesModal
        open={showRegen}
        onClose={() => setShowRegen(false)}
        onComplete={handleRegenComplete}
      />

      {/* 2FA Setup Modal */}
      <Pro2FASetupModal
        open={show2FASetup}
        onClose={() => setShow2FASetup(false)}
        onComplete={handle2FASetupComplete}
      />

      {/* 2FA Disable Modal */}
      <Pro2FADisableModal
        open={show2FADisable}
        onClose={() => setShow2FADisable(false)}
        onComplete={handle2FADisableComplete}
      />
    </div>
  );
}
